import { log } from '@/log'
import { CartSummaryResponse, parseCartSummaryResponse } from '@/models/api/cart/summary'
import { ApiService } from '@/services/api/service'
import { callWithAuth } from '@/services/auth/call-with-auth'
import AppId from '@/util/appid'
import { Either, Left, Right } from 'fp-ts/lib/Either'
import _ from 'lodash'
import { Response } from 'superagent'
import { ApiClient } from './client'

const namespace: string = 'cart'

/**
 * TODO: spa, ajax双方にCartServiceが別れて存在してしまっているので、共通のCartServiceに集約する。
 * @see /client/src/models/api/cart/cart/README.md
 */
export class CartService extends ApiService {
  constructor(appId?: string) {
    super(appId)
  }

  get brandEnglishName(): string {
    return AppId.getBrandNameByAppId(this.appId)
  }

  async list(): Promise<Either<Error, Response>> {
    try {
      const response = await callWithAuth(() => ApiClient.get(`/api/user/cart`))

      log.debug({ service: `${namespace}/list`, response })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }

  /**
   * ブランド別のカートサマリーを取得
   * @param brandName ブランド名（例: 'hl2'）
   * @returns Promise<Either<Error, CartSummaryResponse>>
   */
  async getSummary(brandName: string): Promise<Either<Error, CartSummaryResponse>> {
    try {
      const response = await callWithAuth(() => ApiClient.get(`/api/user/cart/${brandName}/summary`))

      log.debug({ service: `${namespace}/getSummary`, response })

      // レスポンスをパース
      const cartSummaryResponse = parseCartSummaryResponse(response.body.data)

      return new Right(cartSummaryResponse)
    } catch (error) {
      log.error({
        service: `${namespace}/getSummary`,
        error,
      })

      return new Left(error)
    }
  }

  async get(cartId: string, query: any = {}): Promise<Either<Error, Response>> {
    try {
      const response = await ApiClient
        .get(`/api/user-proxy/${this.appId}/commerce_user_cart/${cartId}`)
        .query({
          cart_status: 1,
          ...query,
        })

      log.debug({ service: `${namespace}/get`, response })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }

  async add(
    productId: string,
    unitCount: number,
    unitPrice: number,
    isPaidMembershipPrice: boolean
  ): Promise<Either<Error, Response>> {
    try {
      const response = await ApiClient
        .postJson(`/api/user/cart/${this.brandEnglishName}`)
        .send({
          items: [
            {
              product_id: productId,
              unitCount,
              unitPrice,
              isPaidMembershipPrice,
            },
          ],
        })

      log.debug({ service: `${namespace}/add`, response })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }

  async updateUnitCount(id: string, unitCount: number): Promise<Either<Error, Response>> {
    try {
      const response = await ApiClient
        .put(`/api/user/cart/${this.brandEnglishName}/${id}`)
        .send({
          unitCount,
        })

      log.debug({ service: `${namespace}/updateUnitCount`, response })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }

  /**
   * addやupdateUnitCountメソッドと異なりuser-proxyのAPIを叩いている理由としては、
   * 他のユーザーの商品の購入などが原因でカートの不整合が発生した場合に、
   * ユーザーが自分のカートの商品を削除して整合性を回復できるようにするため。
   * @see https://github.com/Everforth/growthpalette-front/issues/7866#issue-2094596560
   */
  async remove(id: string): Promise<Either<Error, Response>> {
    try {
      const response = await ApiClient
        .delete(`/api/user/cart/${this.brandEnglishName}/${id}`)

      log.debug({ service: `${namespace}/remove`, response })

      return new Right(response)
    } catch (error) {
      log.error({ error })

      return new Left(error)
    }
  }

}

export interface CartServiceStore {
  getServiceForBrand(brand: string): CartService
}
/**
 * カート画面での
 * - 個数変更
 * - 削除 → 元に戻す
 * - 価格変更による再登録
 * 等では、商品のブランド毎に使用するAppIdを変更する必要がある。
 *
 * 一方で、既存のCartServiceは、インスタンス生成時点で使用するAppIdが固定される。
 * そのため、別のappIdを使う都度新しいインスタンスを生成する必要があるが、
 * カンパニーカート等で複数のブランドの商品をまとめて処理する時に、
 * 商品処理の都度新しいオブジェクトを作るのは無駄が多い。
 *
 * そこで、一度使用したAppIdのオブジェクトは保存しておき、
 * 二回目以降は同じオブジェクトを再利用する仕組みを取っている。
 *
 * 将来的にはサーバー側で手当されるべきだが、それまでの一次対応。
 *
 * cf. GoF Flyweightパターン
 *
 * @see https://github.com/my-color/front/pull/3817#issuecomment-635745948
 */
export class CartServiceFlyweightStore implements CartServiceStore {
  private appIdClientMap: Map<string, CartService> = new Map<string, CartService>()

  constructor(
    private readonly factory: (appId: string) => CartService = appId => new CartService(appId)
  ) {}

  getServiceForBrand(brand: string): CartService {
    return this.getServiceFor(AppId.getByBrandName(brand))
  }

  private getServiceFor(appId: string): CartService {
    const service = this.appIdClientMap.get(appId)
    if (service) {
      return service
    }

    const newService = this.factory(appId)
    this.appIdClientMap.set(appId, newService)

    return newService
  }
}
